// 3D replay re-render (REP-3 spectator, M33 remainder closed). A self-contained
// Three.js view that renders the READ-ONLY replay world in 3D while the scrubber
// overlay is open — so a recording plays back as an actual world you watch, not just
// a tick/pose/hash read-out. It owns its own scene, camera, sky, and chunk-mesh set,
// bound to the `ReplayPlayer`'s current world; the main render loop draws THIS scene
// instead of the live one while it is active, so the live world is never disturbed
// (parity — recording/viewing is opt-in and the live scene is left untouched).
//
// The replay world evolves every tick and is rebuilt wholesale on a backward seek
// (the player creates a fresh World from the nearest checkpoint), so each frame we:
//   • rebind the mesh set if the world OBJECT changed (backward seek), and
//   • remesh every loaded chunk when the tick changed (the world content moved).
// Meshing is synchronous (no worker) — a replay area is small and only remeshes on a
// tick change, so this stays cheap. Pure render glue: no sim state is mutated here.
import * as THREE from 'three';
import { PLAYER, VIEW } from '../../sim/config';
import { FOV_DEFAULT } from '../fov';
import { applySky } from './sky';
import { ChunkMeshes } from './chunkmeshes';
import { followCamera } from '../net/spectate';
import type { RenderTable } from '../../sim/mesh/atlasmap';
import type { ReplayPlayer } from '../../sim/replay/player';

/** Spectator camera pose reported to the debug API / E2E. */
export interface ReplayCamPose { x: number; y: number; z: number; yaw: number; pitch: number }

/** The overlay-facing surface of the 3D view (kept structural so the overlay never
 *  imports three). `active` gates whether the main loop draws the replay scene. */
export interface ReplaySceneView {
  readonly active: boolean;
  attach(player: ReplayPlayer): void;
  detach(): void;
  /** Cycle the spectator camera (first-person ↔ over-the-shoulder). */
  cycleCam(): void;
  /** Render the current replay tick and capture it as a PNG data URL (timelapse). */
  takeScreenshot(): string;
}

/** How far the replay scene renders around the spectator (kept modest — a replay
 *  area is small, and a big radius would remesh a lot every tick). */
const REPLAY_RENDER_DIST = Math.min(VIEW.RENDER_DIST_DEFAULT, 6);
/** Over-the-shoulder pull-back for the chase cam (0 = first-person). */
const CHASE_DISTANCE = 4.5;

export class ReplayWorldView implements ReplaySceneView {
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly sky = applySky(this.scene);
  private meshes: ChunkMeshes | null = null;
  private boundWorld: ReplayPlayer['world'] | null = null;
  private player: ReplayPlayer | null = null;
  private lastTick = Number.NaN;
  private camMode: 'first' | 'chase' = 'first';
  private aspect = 0;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly table: RenderTable,
    private readonly material: THREE.Material,
    private readonly waterMaterial: THREE.Material,
    /** The shared chunk-shader daylight uniform (renderer.ts) — we drive it from the
     *  REPLAY world's sky each frame so playback is lit by its own time of day. */
    private readonly daylight: { value: number },
  ) {
    this.camera = new THREE.PerspectiveCamera(FOV_DEFAULT, 1, 0.1, 1000);
    this.camera.rotation.order = 'YXZ';
  }

  /** True while a replay is being rendered in 3D (drives the main-loop branch). */
  get active(): boolean {
    return this.player !== null;
  }

  /** Bind the view to a replay player and start rendering its world (overlay open). */
  attach(player: ReplayPlayer): void {
    this.player = player;
    this.rebind(player.world);
  }

  /** Stop rendering the replay world and release its meshes (overlay close). */
  detach(): void {
    this.player = null;
    this.meshes?.dispose();
    this.meshes = null;
    this.boundWorld = null;
    this.lastTick = Number.NaN;
  }

  cycleCam(): void {
    this.camMode = this.camMode === 'first' ? 'chase' : 'first';
  }

  /** (Re)build the mesh set against a world object (initial attach + backward seek). */
  private rebind(world: ReplayPlayer['world']): void {
    this.meshes?.dispose();
    // No worker: mesh synchronously so a scrub shows the world immediately.
    this.meshes = new ChunkMeshes(world, this.table, this.scene, this.material, this.waterMaterial);
    this.boundWorld = world;
    this.lastTick = Number.NaN;
    this.sky.setDimension(world.dimension === 'nether' ? 'nether' : 'overworld');
  }

  /** Bring the scene up to date with the player's current tick (called each frame
   *  while active). Rebinds on a backward-seek world swap, remeshes on a tick change,
   *  streams chunks around the spectator, and places the camera + sky. */
  sync(): void {
    const player = this.player;
    if (!player || !this.meshes) return;
    if (player.world !== this.boundWorld) this.rebind(player.world);
    const meshes = this.meshes!;
    const pose = player.playerPose();

    if (player.tick !== this.lastTick) {
      meshes.remeshLoaded();            // the world content moved this tick
      this.lastTick = player.tick;
    }
    meshes.update(pose.x, pose.z, REPLAY_RENDER_DIST);

    // Spectator camera: first-person from the replay player's eyes, or a short
    // over-the-shoulder chase. followCamera(distance 0) == first-person.
    const cam = followCamera(
      { x: pose.x, y: pose.y, z: pose.z, yaw: pose.yaw, pitch: pose.pitch },
      { distance: this.camMode === 'chase' ? CHASE_DISTANCE : 0, eyeHeight: PLAYER.EYE_HEIGHT, lift: this.camMode === 'chase' ? 0.4 : 0 },
    );
    this.camera.position.set(cam.x, cam.y, cam.z);
    this.camera.rotation.set(cam.pitch, cam.yaw, 0);
    this.resizeToRenderer();

    // Light the replay world by ITS OWN sky (this frame renders the replay instead of
    // the live scene, so overwriting the shared daylight uniform is safe).
    const skyState = player.world.skyState();
    this.daylight.value = skyState.daylight;
    this.sky.update(skyState, this.camera);
  }

  /** Draw the replay world (the main loop calls this instead of the live render). */
  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /** Sync + render + grab the frame as a PNG data URL (timelapse capture, UI-9). */
  takeScreenshot(): string {
    this.sync();
    this.render();
    return this.renderer.domElement.toDataURL('image/png');
  }

  /** Introspection for the debug API / E2E: is a world being drawn, how many chunk
   *  meshes are resident, and where is the spectator camera. */
  state(): { active: boolean; meshes: number; tick: number; camera: ReplayCamPose } {
    return {
      active: this.active,
      meshes: this.meshes?.loadedCount ?? 0,
      tick: this.player?.tick ?? -1,
      camera: { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z, yaw: this.camera.rotation.y, pitch: this.camera.rotation.x },
    };
  }

  private resizeToRenderer(): void {
    const w = window.innerWidth || 1, h = window.innerHeight || 1;
    const a = w / h;
    if (a !== this.aspect) {
      this.aspect = a;
      this.camera.aspect = a;
      this.camera.updateProjectionMatrix();
    }
  }
}
